/**
 * AccountDetailsAdmin.tsx — Admin > Account > Details.
 *
 * The corporate dashboard's Edit Account dialog (components/accounts-table.tsx)
 * for the signed-in account, as a settings page like Branding (corp lists every
 * account and edits one in a dialog; here there is one account, so the form is
 * the page):
 *   - left: the Logo (the Logos photo folder), with the account's name under it;
 *   - right, in corp's order: Name *, Phone, Country and Currency, Timezone
 *     (the country's zones; a new country clears it), General Intake Form,
 *     Contact Email, Admin Email, User Emails From and System Emails From (each
 *     with its own sender name under "Other"), Auto-log out time and Font Size
 *     Increase;
 *   - then corp's read-only Account Info: the active user counts (in the
 *     account's own role titles), Login Name, Lead E-mail, Comm Log E-mail
 *     Domain, Comm Log Phone and Confirmation Phone, each with a copy button.
 * Save sits top right with Unsaved changes and Revert beside it; changed fields
 * carry the "modified" pill. Only the changed fields are sent, so an
 * auto-logout value legacy stored outside today's list never blocks a save.
 * Country, currency and timezone can be changed but not cleared (the API
 * ignores an empty one, as corp's "— None —" did). Corp's "Add to Photo
 * Library" logo migration stays in the corporate dashboard.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): the form sits in an
 * AdminLockedFieldset, so every field shows its value read-only and the logo
 * can be viewed but not changed; there is no Save or Revert. Account Info's
 * copy buttons still work.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, Check, Copy, RotateCcw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { EntityImageTile } from '@/components/admin/EntityImageTile';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { getAccountSummary, type AccountSummary } from '@/services/accountSummary.service';
import {
  getAccountDetails,
  listCurrencies,
  updateAccountDetails,
  updateGeneralIntakeForm,
  type AccountDetails,
  type AccountDetailsPayload,
  type CurrencyChoice,
} from '@/services/accountDetailsAdmin.service';
import { getErrorMessage } from '@/services/apiClient';
import { resolveLogoUrl } from '@/services/branding.service';
import { listEForms } from '@/services/eformsAdmin.service';
import { listCountryTimezones, type TimezoneChoice } from '@/services/facilities.service';
import { listCountryCodes } from '@/services/patients.service';
import { toast } from '@/store/toastStore';

// ─── Form ──────────────────────────────────────────────────────────────────────

interface AccountForm {
  logo: string;
  name: string;
  phone: string;
  countryID: number | null;
  currencyID: number | null;
  timezone: number | null;
  generalIntakeFormID: number | null;
  contactEmail: string;
  accountEmail: string;
  userEmailsFrom: string;
  userEmailsFromOther: string;
  systemEmailsFrom: string;
  systemEmailsFromOther: string;
  timeout: number;
  fontSizeIncrease: number;
}

const idOrNull = (n: number) => (n > 0 ? n : null);

export function accountFormFrom(d: AccountDetails | null): AccountForm {
  return {
    logo: d?.logo ?? '',
    name: d?.name ?? '',
    phone: d?.phone ?? '',
    countryID: idOrNull(d?.countryID ?? 0),
    currencyID: idOrNull(d?.currencyID ?? 0),
    timezone: idOrNull(d?.timezone ?? 0),
    generalIntakeFormID: idOrNull(d?.generalIntakeFormID ?? 0),
    contactEmail: d?.contactEmail ?? '',
    accountEmail: d?.accountEmail ?? '',
    userEmailsFrom: d?.userEmailsFrom ?? '1',
    userEmailsFromOther: d?.userEmailsFromOther ?? '',
    systemEmailsFrom: d?.systemEmailsFrom ?? '1',
    systemEmailsFromOther: d?.systemEmailsFromOther ?? '',
    timeout: d?.timeout ?? 0,
    fontSizeIncrease: d?.fontSizeIncrease ?? 0,
  };
}

/** The changed fields, as PUT /account-details takes them (the intake form saves separately). */
export function accountPayload(f: AccountForm, changed: Set<keyof AccountForm>): AccountDetailsPayload {
  const out: AccountDetailsPayload = {};
  if (changed.has('logo')) out.logo = f.logo;
  if (changed.has('name')) out.name = f.name.trim();
  if (changed.has('phone')) out.phone = f.phone.trim();
  if (changed.has('contactEmail')) out.contactEmail = f.contactEmail.trim();
  if (changed.has('accountEmail')) out.accountEmail = f.accountEmail.trim();
  if (changed.has('countryID') && f.countryID) out.countryID = f.countryID;
  if (changed.has('currencyID') && f.currencyID) out.currencyID = f.currencyID;
  if (changed.has('timezone') && f.timezone) out.timezone = f.timezone;
  if (changed.has('userEmailsFrom')) out.userEmailsFrom = f.userEmailsFrom;
  if (changed.has('userEmailsFromOther')) out.userEmailsFromOther = f.userEmailsFromOther.trim();
  if (changed.has('systemEmailsFrom')) out.systemEmailsFrom = f.systemEmailsFrom;
  if (changed.has('systemEmailsFromOther')) out.systemEmailsFromOther = f.systemEmailsFromOther.trim();
  if (changed.has('timeout')) out.timeout = f.timeout;
  if (changed.has('fontSizeIncrease')) out.fontSizeIncrease = f.fontSizeIncrease;
  return out;
}

// ─── Options (corp's) ──────────────────────────────────────────────────────────

/** The API accepts only these (config/session_idle.php allowed_seconds). */
const TIMEOUT_OPTIONS = [
  { value: 600, label: '10 min' },
  { value: 900, label: '15 min' },
  { value: 1200, label: '20 min' },
  { value: 1800, label: '30 min' },
  { value: 2700, label: '45 min' },
  { value: 3600, label: '60 min' },
  { value: 5400, label: '90 min' },
  { value: 7200, label: '2 hr' },
  { value: 10800, label: '3 hr' },
  { value: 14400, label: '4 hr' },
];

const USER_EMAILS_FROM_OPTIONS = [
  { value: '1', label: "User's Full Name" },
  { value: '2', label: 'Practice Name' },
  { value: '3', label: 'Other' },
  { value: '4', label: 'User & Practice Name' },
];

const SYSTEM_EMAILS_FROM_OPTIONS = [
  { value: '1', label: 'FollowThatPatient' },
  { value: '2', label: 'Practice Name' },
  { value: '3', label: 'Other' },
];

const FONT_SIZE_OPTIONS = [0, 1, 2, 3, 4].map((n) => ({ value: n, label: n === 0 ? 'Default' : `+${n}` }));

/** A picker's options, plus the saved value when the list no longer has it. */
function withCurrent<V extends string | number>(options: { value: V; label: string }[], current: V | null, label: (v: V) => string) {
  return current == null || current === 0 || options.some((o) => o.value === current) ? options : [...options, { value: current, label: label(current) }];
}

function formatPhone(raw: string): string {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length === 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith('1')) return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return raw ?? '';
}

// ─── Page ──────────────────────────────────────────────────────────────────────

interface Lookups {
  countries: { value: number; label: string }[];
  currencies: CurrencyChoice[];
  eforms: { value: number; label: string }[];
}

export function AccountDetailsAdmin() {
  const queryClient = useQueryClient();
  const locked = useAdminPageLevel() === 'locked';
  const [details, setDetails] = useState<AccountDetails | null>(null);
  const [summary, setSummary] = useState<AccountSummary | null>(null);
  const [lookups, setLookups] = useState<Lookups>({ countries: [], currencies: [], eforms: [] });
  const [timezones, setTimezones] = useState<TimezoneChoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const t = useTrackedForm<AccountForm>(() => accountFormFrom(null));
  const { reset } = t;
  const f = t.form;

  useEffect(() => {
    let cancelled = false;
    const settle = <T,>(p: Promise<T>, fallback: T) => p.catch(() => fallback);
    Promise.all([
      getAccountDetails(),
      settle(getAccountSummary(), null),
      settle(listCountryCodes(), []),
      settle(listCurrencies(), []),
      settle(listEForms(), []),
    ])
      .then(([d, s, countries, currencies, eforms]) => {
        if (cancelled) return;
        setDetails(d);
        setSummary(s);
        const seen = new Set<number>();
        setLookups({
          countries: countries
            .filter((c) => c.countryID > 0 && !seen.has(c.countryID) && seen.add(c.countryID))
            .map((c) => ({ value: c.countryID, label: c.countryName || `Country #${c.countryID}` }))
            .sort((a, b) => a.label.localeCompare(b.label)),
          currencies,
          eforms: eforms
            .filter((e) => e.active)
            .map((e) => ({ value: e.id, label: e.title.trim() || `E-Form #${e.id}` }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        });
        reset(accountFormFrom(d));
      })
      .catch((err) => {
        if (cancelled) return;
        setFailed(true);
        toast.error(getErrorMessage(err, 'The account could not be loaded.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reset]);

  // The country's timezones, reloaded when the country changes.
  useEffect(() => {
    if (!f.countryID) {
      setTimezones([]);
      return;
    }
    let cancelled = false;
    listCountryTimezones(f.countryID)
      .then((list) => !cancelled && setTimezones(list))
      .catch(() => !cancelled && setTimezones([]));
    return () => {
      cancelled = true;
    };
  }, [f.countryID]);

  const nameMissing = !f.name.trim();
  const accountId = details?.accountId ?? 0;

  async function save() {
    if (locked || nameMissing || !t.dirty) return;
    setSaving(true);
    try {
      const payload = accountPayload(f, t.changed);
      if (Object.keys(payload).length > 0) await updateAccountDetails(payload);
      if (t.changed.has('generalIntakeFormID')) await updateGeneralIntakeForm(f.generalIntakeFormID ?? 0);
      reset(f);
      // The profile panel, patient demographics and the navigation read this row.
      void queryClient.invalidateQueries({ queryKey: ['account-details'] });
      void queryClient.invalidateQueries({ queryKey: ['navigation'] });
      toast.success('Account updated');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The account could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  // Locked: nothing can change, so there is no Save or Revert.
  const actions = locked ? undefined : (
    <div className="flex flex-wrap items-center gap-3">
      {t.dirty && (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm text-blue-700">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            Unsaved changes
          </span>
          <Button
            variant="ghost"
            size="xs"
            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
            leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
            aria-label="Revert all changes"
            onClick={t.revertAll}
          >
            Revert
          </Button>
        </div>
      )}
      <Button onClick={() => void save()} disabled={!t.dirty || nameMissing || loading || failed} loading={saving}>
        Save
      </Button>
    </div>
  );

  const timezoneOptions = useMemo(
    () => withCurrent(timezones, f.timezone, (v) => `Timezone #${v}`),
    [timezones, f.timezone],
  );
  const currencyOptions = useMemo(
    () => withCurrent(lookups.currencies.map((c) => ({ value: c.id, label: c.label })), f.currencyID, (v) => `Currency #${v}`),
    [lookups.currencies, f.currencyID],
  );
  const countryOptions = useMemo(() => withCurrent(lookups.countries, f.countryID, (v) => `Country #${v}`), [lookups.countries, f.countryID]);
  const eformOptions = useMemo(
    () => withCurrent(lookups.eforms, f.generalIntakeFormID, (v) => `E-Form #${v} (inactive)`),
    [lookups.eforms, f.generalIntakeFormID],
  );
  const timeoutOptions = useMemo(
    () => withCurrent(TIMEOUT_OPTIONS, f.timeout, (v) => `${Math.round(v / 60)} min (saved)`),
    [f.timeout],
  );

  return (
    <div className="flex h-full flex-col overflow-hidden p-6">
      <div className="flex-shrink-0">
        <PageHeader title="Account Details" subtitle="Your account's name, contact details, region and email senders" actions={actions} />
      </div>
      {loading ? (
        <SpinnerOverlay label="Loading account…" />
      ) : failed ? (
        <p className="py-16 text-center text-sm text-slate-500">The account could not be loaded. Reload the page to try again.</p>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto pb-6">
          <AdminLockedFieldset>
            <div className="flex max-w-4xl flex-col gap-6 md:flex-row">
              {/* Left: logo */}
              <div className="space-y-3 md:w-48 md:shrink-0">
                <EntityImageTile
                  entityType="logo"
                  entityId={null}
                  folder="logos"
                  imageUrl={resolveLogoUrl(f.logo, accountId)}
                  onSelected={(url) => t.patch({ logo: url })}
                  label="Logo"
                  labelExtra={t.badge('logo', 'Logo')}
                />
                {f.name.trim() && <p className="text-sm font-bold text-slate-900">{f.name}</p>}
              </div>

              {/* Right: fields, in corp's order */}
              <div className="min-w-0 flex-1 space-y-4">
                <DialogField
                  label="Name"
                  htmlFor="account-name"
                  required
                  state={nameMissing ? 'error' : 'none'}
                  labelHint={nameMissing ? '— required' : undefined}
                  badge={t.badge('name', 'Name')}
                >
                  <Input
                    id="account-name"
                    value={f.name}
                    onChange={(e) => t.patch({ name: e.target.value })}
                    className={fieldClasses.input(nameMissing && !locked ? 'error' : 'none')}
                  />
                </DialogField>

                <DialogField label="Phone" htmlFor="account-phone" badge={t.badge('phone', 'Phone')}>
                  <Input id="account-phone" type="tel" value={f.phone} onChange={(e) => t.patch({ phone: e.target.value })} />
                </DialogField>

                <div className="grid gap-4 md:grid-cols-2">
                  <DialogField label="Country" htmlFor="account-country" badge={t.badge('countryID', 'Country')}>
                    <Select<number>
                      id="account-country"
                      options={countryOptions}
                      value={f.countryID}
                      // A new country's zones differ: clear the timezone, as corp does.
                      onChange={(v) => v && v !== f.countryID && t.patch({ countryID: v, timezone: null })}
                      placeholder="Select country"
                      searchable
                    />
                  </DialogField>
                  <DialogField label="Currency" htmlFor="account-currency" badge={t.badge('currencyID', 'Currency')}>
                    <Select<number>
                      id="account-currency"
                      options={currencyOptions}
                      value={f.currencyID}
                      onChange={(v) => v && t.patch({ currencyID: v })}
                      placeholder="Select currency"
                      searchable
                    />
                  </DialogField>
                </div>

                <DialogField label="Timezone" htmlFor="account-timezone" badge={t.badge('timezone', 'Timezone')}>
                  <Select<number>
                    id="account-timezone"
                    options={timezoneOptions}
                    value={f.timezone}
                    onChange={(v) => v && t.patch({ timezone: v })}
                    placeholder={f.countryID ? 'Select timezone' : 'Select a country first'}
                    disabled={!f.countryID}
                    searchable
                  />
                </DialogField>

                <DialogField label="General Intake Form" htmlFor="account-intake-form" badge={t.badge('generalIntakeFormID', 'General Intake Form')}>
                  <Select<number>
                    id="account-intake-form"
                    options={eformOptions}
                    value={f.generalIntakeFormID}
                    onChange={(v) => t.patch({ generalIntakeFormID: v ?? null })}
                    placeholder="Select intake form"
                    searchable
                    clearable
                  />
                </DialogField>

                <DialogField label="Contact Email" htmlFor="account-contact-email" badge={t.badge('contactEmail', 'Contact Email')}>
                  <Input
                    id="account-contact-email"
                    type="email"
                    value={f.contactEmail}
                    onChange={(e) => t.patch({ contactEmail: e.target.value })}
                  />
                </DialogField>

                <DialogField label="Admin Email" htmlFor="account-admin-email" badge={t.badge('accountEmail', 'Admin Email')}>
                  <Input
                    id="account-admin-email"
                    type="email"
                    value={f.accountEmail}
                    onChange={(e) => t.patch({ accountEmail: e.target.value })}
                  />
                </DialogField>

                <SenderField
                  id="account-user-emails-from"
                  label="User Emails From"
                  options={USER_EMAILS_FROM_OPTIONS}
                  value={f.userEmailsFrom}
                  other={f.userEmailsFromOther}
                  badge={t.badge('userEmailsFrom', 'User Emails From')}
                  otherBadge={t.badge('userEmailsFromOther', 'User Emails From name')}
                  onChange={(v) => t.patch({ userEmailsFrom: v })}
                  onOtherChange={(v) => t.patch({ userEmailsFromOther: v })}
                />

                <SenderField
                  id="account-system-emails-from"
                  label="System Emails From"
                  options={SYSTEM_EMAILS_FROM_OPTIONS}
                  value={f.systemEmailsFrom}
                  other={f.systemEmailsFromOther}
                  badge={t.badge('systemEmailsFrom', 'System Emails From')}
                  otherBadge={t.badge('systemEmailsFromOther', 'System Emails From name')}
                  onChange={(v) => t.patch({ systemEmailsFrom: v })}
                  onOtherChange={(v) => t.patch({ systemEmailsFromOther: v })}
                />

                <div className="grid gap-4 md:grid-cols-2">
                  <DialogField label="Auto-log out time" htmlFor="account-timeout" badge={t.badge('timeout', 'Auto-log out time')}>
                    <Select<number>
                      id="account-timeout"
                      options={timeoutOptions}
                      value={f.timeout || null}
                      onChange={(v) => v && t.patch({ timeout: v })}
                      placeholder="Default (30 min)"
                    />
                  </DialogField>
                  <DialogField label="Font Size Increase" htmlFor="account-font-size" badge={t.badge('fontSizeIncrease', 'Font Size Increase')}>
                    <Select<number>
                      id="account-font-size"
                      options={FONT_SIZE_OPTIONS}
                      value={f.fontSizeIncrease}
                      onChange={(v) => t.patch({ fontSizeIncrease: v ?? 0 })}
                    />
                  </DialogField>
                </div>

                {details && <AccountInfo details={details} summary={summary} />}
              </div>
            </div>
          </AdminLockedFieldset>
        </div>
      )}
    </div>
  );
}

// ─── Pieces ────────────────────────────────────────────────────────────────────

function SenderField({
  id,
  label,
  options,
  value,
  other,
  badge,
  otherBadge,
  onChange,
  onOtherChange,
}: {
  id: string;
  label: string;
  options: { value: string; label: string }[];
  value: string;
  other: string;
  badge: ReactNode;
  otherBadge: ReactNode;
  onChange: (v: string) => void;
  onOtherChange: (v: string) => void;
}) {
  return (
    <DialogField label={label} htmlFor={id} badge={badge}>
      <Select<string>
        id={id}
        options={withCurrent(options, value, (v) => `Option ${v}`)}
        value={value}
        onChange={(v) => v && onChange(v)}
      />
      {value === '3' && (
        <div className="flex items-center gap-2">
          <Input
            id={`${id}-other`}
            aria-label={`${label}: sender name`}
            placeholder="Enter custom sender name"
            value={other}
            onChange={(e) => onOtherChange(e.target.value)}
            wrapperClassName="min-w-0 flex-1"
          />
          {otherBadge}
        </div>
      )}
    </DialogField>
  );
}

/** Corp's read-only Account Info box. */
function AccountInfo({ details, summary }: { details: AccountDetails; summary: AccountSummary | null }) {
  const roles = summary?.roleTitles;
  const counts = summary?.counts;
  const leadEmail = details.domainEmailLead ? `contact${details.accountId}@${details.domainEmailLead}` : '';
  const replyDomain = details.domainEmailReply ? `@${details.domainEmailReply.replace(/^@/, '')}` : '';
  return (
    <section className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4" aria-label="Account Info">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Account Info</p>
      <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 md:grid-cols-4">
        <Info label="Active Users" value={String(counts?.users ?? 0)} />
        <Info label={roles?.doctor ?? 'Doctor'} value={String(counts?.doctors ?? 0)} />
        <Info label={roles?.coordinator ?? 'Coordinator'} value={String(counts?.coordinators ?? 0)} />
        <Info label={roles?.nurse ?? 'Nurse'} value={String(counts?.nurses ?? 0)} />
      </div>
      <Info label="Login Name" value={details.loginName} />
      <div className="grid gap-3 text-sm md:grid-cols-2">
        <Info label="Lead E-mail" value={leadEmail} copy />
        <Info label="Comm Log E-mail Domain" value={replyDomain} copy />
      </div>
      <div className="grid gap-3 text-sm md:grid-cols-2">
        <Info label="Comm Log Phone" value={formatPhone(details.smsReplyNumber)} copyValue={details.smsReplyNumber} copy />
        <Info label="Confirmation Phone" value={formatPhone(details.smsConfirmNumber)} copyValue={details.smsConfirmNumber} copy />
      </div>
    </section>
  );
}

function Info({ label, value, copy = false, copyValue }: { label: string; value: string; copy?: boolean; copyValue?: string }) {
  return (
    <div className="min-w-0 text-sm">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="flex items-center break-all font-medium text-slate-900">
        <span>{value || '—'}</span>
        {copy && value && <CopyButton label={label} value={copyValue || value} />}
      </p>
    </div>
  );
}

function CopyButton({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="ml-1.5 inline-flex shrink-0 text-slate-400 transition-colors hover:text-slate-700"
      aria-label={`Copy ${label}`}
      title="Copy to clipboard"
    >
      {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}
